import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { SearchStatus, TravelTypes } from '../../constants';

/*
Properties of each object inside brochures
{
  fromEmail,
  createdFor,
  emailRecipients,
  emailSubject,
  emailMessage,
  doSendCopyToAgent,
  doUpdatePriceOnPageLoad,
  trip: {
      tripSeg: [{
        itinerary: {
          mode: TravelTypes.hotel,
          ...
        }
      }],
  }
  isSavedToDatabase: true,
  saveBrochureStatus,
  sendBrochureStatus,
  saveBrochureError,
  emailFormValidations,
  updateBrochurePriceStatus,
  updateBrochurePriceError,
}
*/
const initialState = {
  getBrochureStatus: SearchStatus.INITIAL,
  getBrochureError: null,
  brochure: {
    createdFor: '',
    fromEmail: '',
    emailRecipientsString: '',
    emailSubject: '',
    doSendCopyToAgent: false,
    doUpdatePriceOnPageLoad: undefined,
    doAllowBook: undefined,
    trips: {},
    doShowBrochureEditor: false,
    editorHTML: '',
    editorJSON: '',
    isSaveBrochureContent: false,
    templateId: 'classic',
  },
  productsCodesAddedToBrochure: [],
  productsCodesAddingToBrochure: [],
  brochures: {},
  error: {},
};

const brochureSlice = createSlice({
  name: 'brochure',
  initialState,
  reducers: {
    resetBrochureState: () => initialState,
    updateBrochureField: (state, { payload: { fieldName, fieldValue } }) => ({
      ...state,
      brochure: {
        ...state.brochure,
        [fieldName]: fieldValue,
      },
    }),
    updateBrochureFields: (state, { payload: { fields } }) => ({
      ...state,
      brochure: {
        ...state.brochure,
        ...fields,
      },
    }),
    updateStoredBrochureFields: (state, { payload: { brochureId, fields } }) => ({
      ...state,
      brochures: {
        ...state.brochures,
        [brochureId]: {
          ...state.brochures[brochureId],
          ...fields,
        },
      },
    }),
    addToBrochure: (state, { payload: { tripId, updatedTrip, staterooms } }) => {
      const updatedTrips = {
        ...state.brochure.trips,
        [tripId]: {
          ...updatedTrip,
          staterooms,
        },
      };

      let shouldRemoveTrip = updatedTrip.tripSegments.length === 0;

      if (
        updatedTrip.tripSegments.length === 1 &&
        updatedTrip.tripSegments[0].itinerary.mode === TravelTypes.cruise
      ) {
        const cabinCategories = _.get(
          updatedTrip,
          'tripSegments[0].itinerary.cabinCategories[0].cabinCategories',
        );
        // if it hasn't any cabinCategories - remove this trip
        shouldRemoveTrip = cabinCategories.length === 0;
      }
      return {
        ...state,
        brochure: {
          ...state.brochure,
          trips: shouldRemoveTrip ? _.omit(updatedTrips, tripId) : updatedTrips,
          isSavedToDatabase: false,
        },
      };
    },
    removeTripFromBrochure: (state) => ({
      ...state,
      brochure: {
        trips: {},
      },
    }),
    saveBrochureRequest: (state, action) => ({
      ...state,
      brochure: {
        ...state.brochure,
        saveBrochureStatus:
          action && action.payload && action.payload.doSaveToDatabase
            ? SearchStatus.SEARCHING
            : SearchStatus.INITIAL,
        saveBrochureError: null,
        sendBrochureStatus: SearchStatus.INITIAL,
        sendBrochureError: null,
        sendBrochureRequestStatus: SearchStatus.INITIAL,
      },
    }),
    saveBrochureResponse: (
      state,
      { payload: { error, response, doSaveToDatabase, templateId } },
    ) => {
      let brochure = {};
      let brochures = {};
      if (error) {
        brochure = {
          saveBrochureError: error,
          saveBrochureStatus: doSaveToDatabase ? SearchStatus.DONE : SearchStatus.INITIAL,
          sendBrochureRequestStatus: SearchStatus.DONE,
        };
      } else {
        const brochureId = (response || {}).id;
        brochure = {
          isSavedToDatabase: !!doSaveToDatabase,
          saveBrochureStatus: doSaveToDatabase ? SearchStatus.DONE : SearchStatus.INITIAL,
          sendBrochureRequestStatus: SearchStatus.DONE,
          id: brochureId,
        };
        if (brochureId) {
          brochures = {
            [brochureId]: {
              isSavedToDatabase: !!doSaveToDatabase,
              ...response,
              ...(templateId ? { templateId } : {}),
            },
          };
        }
      }
      return {
        ...state,
        brochure: {
          ...state.brochure,
          ...brochure,
          isSaveBrochureContent: false,
        },
        brochures: {
          ...state.brochures,
          ...brochures,
        },
      };
    },
    getBrochureRequest: (state) => ({
      ...state,
      getBrochureStatus: SearchStatus.SEARCHING,
      getBrochureError: null,
    }),
    getBrochureResponse: (state, action) => ({
      ...state,
      isViewingBrochure: true,
      getBrochureStatus: SearchStatus.DONE,
      getBrochureError: '',
      brochures: {
        ...state.brochures,
        [action.payload.id]: {
          ...action.payload,
        },
      },
    }),
    getBrochureFail: (state, { payload: { error } }) => ({
      ...state,
      getBrochureError: error,
      getBrochureStatus: SearchStatus.DONE,
    }),
    sendBrochureRequest: (state) => {
      const updatedObj = {
        sendBrochureStatus: SearchStatus.SEARCHING,
        sendBrochureError: null,
        saveBrochureStatus: SearchStatus.INITIAL,
        saveBrochureError: null,
      };
      return {
        ...state,
        brochure: {
          ...state.brochure,
          ...updatedObj,
        },
      };
    },
    sendBrochureResponse: (state, { payload: { error } }) => ({
      ...state,
      brochure: {
        ...state.brochure,
        sendBrochureError: error,
        sendBrochureStatus: SearchStatus.DONE,
      },
    }),
    changeValidation: (state, { payload: { validations } }) => ({
      ...state,
      brochure: {
        ...state.brochure,
        emailFormValidations: validations,
      },
    }),
    setIsViewingBrochure: (state, { payload }) => ({
      ...state,
      isViewingBrochure: payload,
    }),
    updateBrochurePriceRequest: (state) => ({
      ...state,
      updateBrochurePriceStatus: SearchStatus.SEARCHING,
      updateBrochurePriceError: null,
    }),
    updateBrochurePriceResponse: (state, action) => ({
      ...state,
      updateBrochurePriceError: null,
      updateBrochurePriceStatus: SearchStatus.DONE,
      brochures: {
        ...state.brochures,
        [action.payload.id]: {
          ...action.payload,
          isSavedToDatabase: true,
        },
      },
    }),
    updateBrochurePriceFail: (state, { payload: { error } }) => ({
      ...state,
      updateBrochurePriceError: error,
      updateBrochurePriceStatus: SearchStatus.DONE,
    }),
    setProductsCodesAddedToBrochure: (state, { payload: { productCode } }) => ({
      ...state,
      productsCodesAddedToBrochure: state.productsCodesAddedToBrochure.includes(productCode)
        ? state.productsCodesAddedToBrochure.filter((code) => code !== productCode)
        : [...state.productsCodesAddedToBrochure, productCode],
    }),
    setProductsCodesAddingToBrochure: (state, { payload: { productCodes } }) => ({
      ...state,
      productsCodesAddingToBrochure: productCodes,
    }),
  },
});

export const {
  updateBrochureField,
  updateBrochureFields,
  updateStoredBrochureFields,
  addToBrochure,
  removeTripFromBrochure,
  saveBrochureRequest,
  saveBrochureResponse,
  getBrochureRequest,
  getBrochureResponse,
  getBrochureFail,
  sendBrochureRequest,
  sendBrochureResponse,
  changeValidation,
  setIsViewingBrochure,
  updateBrochurePriceResponse,
  updateBrochurePriceRequest,
  updateBrochurePriceFail,
  setProductsCodesAddedToBrochure,
  setProductsCodesAddingToBrochure,
  resetBrochureState,
} = brochureSlice.actions;
export default brochureSlice.reducer;
